import { ExportError } from "../export/errors";
import { emitExportTelemetry } from "../export/exportTelemetry";
import { mergeRetryPolicy } from "../export/retryPolicy";
import { runWithRetry } from "../export/retryExecutor";
import { renderPdfDocument } from "../pdf/renderPdfDocument";
import { renderPptDocument } from "../ppt/renderPptDocument";
import { sanitizeFilename } from "../ppt/sanitizeFilename";
import { isLibrarySaveEnabled, saveLibraryItem } from "../../api/library";
import { buildSaveLockKey, createSaveId, saveLockManager } from "./libraryLock";

const INCLUDE_PPT_ON_SAVE =
  (process.env.REACT_APP_SAVE_INCLUDE_PPT ?? "true").trim().toLowerCase() !== "false";

const INCLUDE_SNAPSHOT_ON_SAVE =
  (process.env.REACT_APP_SAVE_INCLUDE_SNAPSHOT ?? "true").trim().toLowerCase() !== "false";

function ensureBlobType(source, type) {
  if (source instanceof Blob) {
    if (!source.type || source.type === type) {
      return new Blob([source], { type });
    }
    return source;
  }
  return new Blob([source], { type });
}

function pdfDocumentToBlob(pdf) {
  return ensureBlobType(pdf.output("blob"), "application/pdf");
}

async function pptDocumentToBlob(pptx) {
  return pptx.write({ outputType: "blob" });
}

export async function executeSaveToLibraryWorkflow(options) {
  if (!isLibrarySaveEnabled()) {
    throw new ExportError("Library save is not enabled in this environment.", {
      phase: "storage",
      isTransient: false,
    });
  }

  const saveId = options.context?.saveId ?? createSaveId();
  const context = {
    saveId,
    toolId: options.context?.toolId,
    contentKey: options.context?.contentKey,
    title: options.context?.title,
  };

  const policy = mergeRetryPolicy(options.policy);
  const lockKey = buildSaveLockKey(context);
  const staleLockAgeMs = (policy.timeoutMs ?? 30_000) * policy.maxAttempts * 2;
  saveLockManager.cleanupStale(staleLockAgeMs);

  if (!saveLockManager.tryAcquire(lockKey, saveId)) {
    throw new ExportError("A save is already in progress for this item.", {
      phase: "storage",
      isTransient: false,
    });
  }

  const startedAt = Date.now();

  try {
    emitExportTelemetry("library_save_started", {
      exportId: saveId,
      toolId: context.toolId,
      contentKey: context.contentKey,
      format: "library",
    });

    const document = options.document;
    const safeKey = sanitizeFilename(context.contentKey ?? context.title ?? "export");

    const formatResult = await runWithRetry(
      async () => {
        const pdf = await options.formatPdf?.();
        if (!pdf) {
          const rendered = renderPdfDocument(document);
          return pdfDocumentToBlob(rendered);
        }
        return ensureBlobType(pdf, "application/pdf");
      },
      {
        policy,
        context: { ...context, format: "pdf", exportId: saveId },
        phase: "formatting",
        onRetry: options.onRetry,
        signal: options.signal,
      },
    );

    let pptBlob = null;
    if (INCLUDE_PPT_ON_SAVE) {
      const pptResult = await runWithRetry(
        async () => {
          const rendered = renderPptDocument(document);
          return pptDocumentToBlob(rendered);
        },
        {
          policy,
          context: { ...context, format: "ppt", exportId: saveId },
          phase: "formatting",
          onRetry: options.onRetry,
          signal: options.signal,
        },
      );
      pptBlob = pptResult.result;
    }

    const storeResult = await runWithRetry(
      async () => {
        const formData = new FormData();
        formData.append("toolId", context.toolId ?? "unknown");
        formData.append("contentKey", safeKey);
        formData.append("title", context.title ?? safeKey);
        if (options.subtitle) {
          formData.append("subtitle", options.subtitle);
        }
        if (options.metadata) {
          formData.append("metadata", JSON.stringify(options.metadata));
        }
        if (INCLUDE_SNAPSHOT_ON_SAVE && options.contentSnapshot) {
          formData.append("snapshotJson", JSON.stringify(options.contentSnapshot));
        }
        if (options.rootItemId) {
          formData.append("rootItemId", options.rootItemId);
        }
        if (options.versionLabel) {
          formData.append("versionLabel", options.versionLabel);
        }

        const pdfBlob = ensureBlobType(formatResult.result, "application/pdf");
        formData.append("pdfFile", pdfBlob, `${safeKey}.pdf`);

        if (pptBlob) {
          formData.append(
            "pptFile",
            ensureBlobType(
              pptBlob,
              "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            ),
            `${safeKey}.pptx`,
          );
        }

        return saveLibraryItem(formData, { signal: options.signal });
      },
      {
        policy,
        context: { ...context, format: "library", exportId: saveId },
        phase: "storage",
        onRetry: options.onRetry,
        signal: options.signal,
      },
    );

    const durationMs = Date.now() - startedAt;
    emitExportTelemetry(
      "library_save_completed",
      {
        exportId: saveId,
        toolId: context.toolId,
        contentKey: context.contentKey,
        format: "library",
      },
      { durationMs },
    );

    return {
      saveId,
      response: storeResult.result,
      durationMs,
    };
  } catch (error) {
    emitExportTelemetry(
      "library_save_failed",
      {
        exportId: saveId,
        toolId: context.toolId,
        contentKey: context.contentKey,
        format: "library",
      },
      {
        errorMessage: error instanceof Error ? error.message : String(error),
      },
    );
    throw error;
  } finally {
    saveLockManager.release(lockKey, saveId);
  }
}
